/**
 * DryPull Animation - "Comedy Sad Moment" (ENHANCED VERSION)
 * 
 * FRAMER MOTION VERSION: Ultimate comedy disappointment experience!
 * - Hope sparkle fake-out that builds anticipation
 * - Record scratch visual moment of realization
 * - Multiple tears for maximum sadness
 * - Deflating wrapper physics
 * - Rare moth flying out (15% chance)
 * - Cricket chirp visual lines for awkward silence
 * - RIP gravestone popup
 * - Floating sad emojis
 * - Tumbleweed rolling by
 * 
 * Synchronized with comedic sound effects.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useAnimationSound from '../../../hooks/useAnimationSound';
import { getPhasesForCandy, getRandomDryPullFlow } from './soundRegistry';

const DryPullAnimation = ({ onComplete }) => {
    const [phase, setPhase] = useState('hopeful');

    // Random sound flow selection (one of 5 themed flows)
    // Selected once on mount so the entire animation uses the same theme
    const flowId = useMemo(() => getRandomDryPullFlow(), []);

    // Sound integration (DryPull uses max tiers for all the meme sounds)
    const { playPhaseSound, preloadSounds } = useAnimationSound('DryPull', 6, true, flowId);

    // Random sad texts (expanded list)
    const sadTexts = useMemo(() => [
        'Empty...', 'Oof...', 'Bruh', 'F', 'Rip',
        'Next time!', 'So close...', '...', 'Nope!', 'Sadge',
        'Why?!', 'pain.', 'L', 'down bad', 'cope',
    ], []);
    const sadText = useMemo(() => sadTexts[Math.floor(Math.random() * sadTexts.length)], [sadTexts]);

    // Random chance effects
    const showTumbleweed = useMemo(() => Math.random() > 0.5, []);
    const showMoth = useMemo(() => Math.random() < 0.15, []); // 15% chance for rare moth
    const showGravestone = useMemo(() => Math.random() > 0.6, []); // 40% chance

    // Sad emojis pool
    const sadEmojis = useMemo(() => ['😢', '😭', '💀', '🥺', '😿'], []);
    const floatingEmojis = useMemo(() =>
        Array.from({ length: 3 }, (_, i) => ({
            id: i,
            emoji: sadEmojis[Math.floor(Math.random() * sadEmojis.length)],
            x: -40 + Math.random() * 80,
            delay: 0.2 + i * 0.25,
        })), [sadEmojis]);

    // Stable ref for playPhaseSound to avoid re-triggering animation useEffect
    const playPhaseSoundRef = React.useRef(playPhaseSound);
    playPhaseSoundRef.current = playPhaseSound;

    // Stable ref for onComplete
    const onCompleteRef = React.useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Preload sounds (only once on mount)
    useEffect(() => {
        preloadSounds(getPhasesForCandy('DryPull'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extended animation sequence with more phases (now 3.2s total)
    // Uses ref to avoid re-triggering when playPhaseSound reference changes
    useEffect(() => {
        const timers = [];

        playPhaseSoundRef.current('hopeful');

        timers.push(setTimeout(() => {
            setPhase('revealing');
            playPhaseSoundRef.current('revealing');
        }, 600));

        timers.push(setTimeout(() => {
            setPhase('crumpling');
            playPhaseSoundRef.current('crumpling');
        }, 1100));

        timers.push(setTimeout(() => {
            setPhase('drifting');
            playPhaseSoundRef.current('drifting');
        }, 1800));

        timers.push(setTimeout(() => {
            setPhase('complete');
            onCompleteRef.current?.();
        }, 3200));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dust particles
    const dustParticles = useMemo(() =>
        Array.from({ length: 12 }, (_, i) => ({
            id: i,
            x: -40 + Math.random() * 80,
            y: -15 + Math.random() * 30,
            delay: 0.1 + Math.random() * 0.4,
            size: 2 + Math.random() * 3,
        })), []);

    // Multiple tears
    const tears = useMemo(() => [
        { id: 0, left: '35%', delay: 0 },
        { id: 1, left: '60%', delay: 0.4 },
        { id: 2, left: '40%', delay: 0.8 },
    ], []);

    // Cricket chirp sound waves
    const cricketWaves = useMemo(() =>
        Array.from({ length: 3 }, (_, i) => ({
            id: i,
            delay: 0.3 + i * 0.2,
        })), []);

    return (
        <div className="relative flex items-center justify-center overflow-visible w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64">

            {/* === HOPE PHASE: Golden sparkle fake-out === */}
            <AnimatePresence>
                {phase === 'hopeful' && (
                    <>
                        {/* Fake golden glow */}
                        <motion.div
                            className="absolute w-40 h-40 rounded-full"
                            style={{
                                background: 'radial-gradient(circle, rgba(255,215,0,0.4) 0%, transparent 70%)',
                            }}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: [0, 1.2, 1], opacity: [0, 0.8, 0.6] }}
                            exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
                            transition={{ duration: 0.5 }}
                        />
                        {/* Sparkle particles */}
                        {Array.from({ length: 6 }).map((_, i) => (
                            <motion.div
                                key={`sparkle-${i}`}
                                className="absolute w-2 h-2"
                                style={{
                                    background: '#FFD700',
                                    clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                                    boxShadow: '0 0 8px #FFD700',
                                }}
                                initial={{ scale: 0, opacity: 0, rotate: 0 }}
                                animate={{
                                    x: Math.cos((i * 60) * Math.PI / 180) * 50,
                                    y: Math.sin((i * 60) * Math.PI / 180) * 50,
                                    scale: [0, 1, 0],
                                    opacity: [0, 1, 0],
                                    rotate: 180,
                                }}
                                transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }}
                            />
                        ))}
                        {/* Exciting "!" */}
                        <motion.span
                            className="absolute text-3xl font-bold text-yellow-400"
                            style={{ top: '10%', textShadow: '0 0 10px rgba(255,215,0,0.8)' }}
                            initial={{ scale: 0, y: 10 }}
                            animate={{ scale: [0, 1.3, 1], y: 0 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                        >
                            !
                        </motion.span>
                    </>
                )}
            </AnimatePresence>

            {/* === REVEAL PHASE: Record scratch moment === */}
            <AnimatePresence>
                {phase === 'revealing' && (
                    <>
                        {/* Record scratch visual */}
                        <motion.div
                            className="absolute inset-0 flex items-center justify-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 0.3 }}
                        >
                            <div
                                className="w-48 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent"
                                style={{ transform: 'rotate(-5deg)' }}
                            />
                        </motion.div>
                        {/* Question Marks */}
                        {[{ x: -35, delay: 0.1 }, { x: 35, delay: 0.25 }, { x: 0, delay: 0.4 }].map((q, i) => (
                            <motion.span
                                key={i}
                                className="absolute text-2xl font-bold text-gray-400"
                                style={{ top: '15%' }}
                                initial={{ opacity: 0, y: 15, x: q.x, scale: 0.5 }}
                                animate={{ opacity: [0, 1, 1, 0], y: [15, -15], scale: [0.5, 1.2, 1, 0.8] }}
                                transition={{ duration: 0.7, delay: q.delay }}
                            >
                                ?
                            </motion.span>
                        ))}
                    </>
                )}
            </AnimatePresence>

            {/* Gray sad overlay */}
            <AnimatePresence>
                {(phase === 'crumpling' || phase === 'drifting') && (
                    <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ background: 'radial-gradient(circle, rgba(80,80,80,0.35) 0%, transparent 70%)' }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                    />
                )}
            </AnimatePresence>

            {/* Empty Wrapper with enhanced deflating physics */}
            <motion.div
                className="relative"
                animate={
                    phase === 'hopeful' ? {
                        scale: [0.8, 1.05, 1],
                        opacity: [0, 1],
                        y: [10, 0],
                    } :
                        phase === 'revealing' ? {
                            y: [0, -8, 0, -4, 0],
                            scale: [1, 1.02, 0.98, 1],
                        } :
                            phase === 'crumpling' ? {
                                scale: [1, 0.9, 0.82],
                                rotate: [-2, -8],
                                y: [0, 5],
                                scaleY: 0.85,
                            } :
                                phase === 'drifting' ? {
                                    x: [0, 25],
                                    rotate: -12,
                                    opacity: 0.6,
                                    scaleY: 0.8,
                                    y: 8,
                                } : {}
                }
                transition={{
                    duration: phase === 'revealing' ? 0.5 : 0.5,
                    ease: phase === 'crumpling' ? 'easeOut' : 'easeInOut',
                }}
            >
                {/* Wrapper Body */}
                <motion.div
                    className="w-24 h-16 relative"
                    style={{
                        background: 'linear-gradient(135deg, #E8D5C4 0%, #D4C4B4 50%, #C4B4A4 100%)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    }}
                    animate={phase === 'crumpling' ? {
                        borderRadius: ['8px', '12px'],
                    } : {}}
                >
                    {/* Wrapper Folds - more pronounced in crumple */}
                    {[0, 1, 2, 3].map((i) => (
                        <motion.div
                            key={i}
                            className="absolute h-[2px]"
                            style={{
                                background: 'rgba(140, 120, 100, 0.5)',
                                width: `${50 + i * 5}%`,
                                left: `${25 - i * 2.5}%`,
                                top: `${25 + i * 15}%`,
                            }}
                            animate={phase === 'crumpling' ? {
                                scaleX: [1, 0.7 + Math.random() * 0.4],
                                y: [0, 2 - Math.random() * 4],
                            } : {}}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                        />
                    ))}

                    {/* Twisted Ends - droop when crumpling */}
                    <motion.div
                        className="absolute w-6 h-8 -left-5 top-1/2 -translate-y-1/2"
                        style={{
                            background: 'linear-gradient(90deg, #C4B4A4 0%, #E8D5C4 100%)',
                            borderRadius: '4px 0 0 4px',
                            clipPath: 'polygon(100% 20%, 0% 50%, 100% 80%)',
                            transformOrigin: 'right center',
                        }}
                        animate={phase === 'crumpling' || phase === 'drifting' ? {
                            rotate: -15,
                            y: 3,
                        } : {}}
                    />
                    <motion.div
                        className="absolute w-6 h-8 -right-5 top-1/2 -translate-y-1/2"
                        style={{
                            background: 'linear-gradient(270deg, #C4B4A4 0%, #E8D5C4 100%)',
                            borderRadius: '0 4px 4px 0',
                            clipPath: 'polygon(0% 20%, 100% 50%, 0% 80%)',
                            transformOrigin: 'left center',
                        }}
                        animate={phase === 'crumpling' || phase === 'drifting' ? {
                            rotate: 15,
                            y: 3,
                        } : {}}
                    />

                    {/* Sad Face with enhanced expressions */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        {/* Sad Eyes */}
                        <div className="flex gap-4 mb-1">
                            {[0, 1].map((i) => (
                                <div key={i} className="relative">
                                    {/* Eyebrow - more droopy */}
                                    <motion.div
                                        className="absolute -top-2 w-5 h-1 rounded-full"
                                        style={{
                                            background: '#7B6655',
                                            transformOrigin: i === 0 ? 'right center' : 'left center',
                                        }}
                                        animate={{
                                            rotate: phase === 'hopeful' ? (i === 0 ? -5 : 5) :
                                                phase === 'crumpling' ? (i === 0 ? 20 : -20) :
                                                    phase === 'drifting' ? (i === 0 ? 25 : -25) :
                                                        (i === 0 ? 10 : -10),
                                            y: phase === 'crumpling' ? 2 : 0,
                                        }}
                                        transition={{ duration: 0.4 }}
                                    />
                                    {/* Eye */}
                                    <motion.div
                                        className="w-5 h-5 bg-white rounded-full flex items-center justify-center overflow-hidden"
                                        style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)' }}
                                        animate={phase === 'crumpling' ? { scaleY: 0.7 } : {}}
                                    >
                                        <motion.div
                                            className="w-2 h-2 bg-gray-700 rounded-full"
                                            animate={{
                                                y: phase === 'hopeful' ? -1 :
                                                    phase === 'drifting' ? 3 :
                                                        phase === 'crumpling' ? 2 : 0,
                                                scale: phase === 'crumpling' ? 0.8 : 1,
                                            }}
                                        />
                                    </motion.div>
                                </div>
                            ))}
                        </div>

                        {/* Sad Mouth - more expressive */}
                        <motion.div
                            className="relative"
                            animate={{
                                y: phase === 'crumpling' ? 2 : 0,
                            }}
                        >
                            <motion.div
                                className="w-7 h-3 border-b-2 border-[#7B6655] rounded-b-full"
                                style={{ transform: 'scaleY(-1)' }}
                                animate={{
                                    scaleX: phase === 'crumpling' ? 0.7 :
                                        phase === 'drifting' ? 0.6 : 1,
                                    scaleY: phase === 'crumpling' ? -1.3 : -1,
                                }}
                            />
                        </motion.div>
                    </div>

                    {/* Multiple Tear Drops */}
                    <AnimatePresence>
                        {(phase === 'crumpling' || phase === 'drifting') && tears.map((tear) => (
                            <motion.div
                                key={tear.id}
                                className="absolute w-3 h-4"
                                style={{
                                    left: tear.left,
                                    top: '45%',
                                    background: 'linear-gradient(180deg, rgba(135,206,250,0.9) 0%, rgba(65,105,225,0.7) 100%)',
                                    borderRadius: '50% 50% 50% 50% / 30% 30% 70% 70%',
                                }}
                                initial={{ y: 0, opacity: 0, scale: 0 }}
                                animate={{
                                    y: [0, 35, 45],
                                    opacity: [0, 1, 1, 0],
                                    scale: [0, 1.1, 1, 0.6],
                                }}
                                transition={{
                                    duration: 1.8,
                                    ease: 'easeIn',
                                    delay: tear.delay,
                                }}
                            />
                        ))}
                    </AnimatePresence>
                </motion.div>
            </motion.div>

            {/* === CRUMPLING PHASE: Dust Particles === */}
            <AnimatePresence>
                {phase === 'crumpling' && dustParticles.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-full"
                        style={{
                            width: p.size,
                            height: p.size,
                            background: 'rgba(160, 140, 120, 0.6)',
                        }}
                        initial={{ x: 0, y: 0, opacity: 0 }}
                        animate={{
                            x: p.x,
                            y: p.y + 25,
                            opacity: [0, 0.7, 0],
                            scale: [0.5, 1, 0.3],
                        }}
                        transition={{ duration: 1, delay: p.delay }}
                    />
                ))}
            </AnimatePresence>

            {/* === DRIFTING PHASE: All the comedy elements === */}

            {/* Tumbleweed */}
            <AnimatePresence>
                {showTumbleweed && phase === 'drifting' && (
                    <motion.div
                        className="absolute bottom-2"
                        initial={{ x: -180, rotate: 0 }}
                        animate={{ x: 180, rotate: 1080 }}
                        transition={{ duration: 2.5, ease: 'linear' }}
                    >
                        <div
                            className="w-10 h-10 rounded-full"
                            style={{
                                background: 'radial-gradient(circle at 30% 30%, #D2B48C 15%, transparent 15%, transparent 35%, #C4A882 35%, #C4A882 45%, transparent 45%)',
                                border: '3px solid #B8986A',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Rare Moth flying out */}
            <AnimatePresence>
                {showMoth && phase === 'drifting' && (
                    <motion.div
                        className="absolute text-lg"
                        initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
                        animate={{
                            x: [0, -20, -60, -40, -100],
                            y: [0, -30, -20, -50, -70],
                            opacity: [0, 1, 1, 0.8, 0],
                            scale: [0.3, 0.6, 0.5, 0.4, 0.3],
                            rotate: [0, -10, 10, -5, 0],
                        }}
                        transition={{ duration: 1.8, ease: 'easeOut' }}
                    >
                        🦋
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Cricket Chirp Sound Waves */}
            <AnimatePresence>
                {phase === 'drifting' && cricketWaves.map((wave) => (
                    <motion.div
                        key={wave.id}
                        className="absolute"
                        style={{ right: '-40px', top: '50%' }}
                        initial={{ opacity: 0, x: 0 }}
                        animate={{ opacity: [0, 0.6, 0], x: [0, 15, 30] }}
                        transition={{ duration: 0.6, delay: wave.delay }}
                    >
                        <div className="flex gap-1">
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={i}
                                    className="w-1 rounded-full bg-gray-400"
                                    style={{ height: 8 + (i % 2) * 6 }}
                                    animate={{ scaleY: [1, 1.5, 1] }}
                                    transition={{
                                        duration: 0.15,
                                        repeat: 2,
                                        delay: i * 0.05,
                                    }}
                                />
                            ))}
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* RIP Gravestone */}
            <AnimatePresence>
                {showGravestone && phase === 'drifting' && (
                    <motion.div
                        className="absolute"
                        style={{ bottom: '5%', right: '15%' }}
                        initial={{ y: 30, opacity: 0, scale: 0 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
                    >
                        <div
                            className="w-10 h-12 bg-gray-600 relative"
                            style={{
                                borderRadius: '8px 8px 0 0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            }}
                        >
                            <span
                                className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-300"
                                style={{ fontFamily: 'serif' }}
                            >
                                RIP
                            </span>
                        </div>
                        <div className="w-12 h-2 bg-gray-700 -ml-1 rounded-sm" />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Sad Emojis */}
            <AnimatePresence>
                {phase === 'drifting' && floatingEmojis.map((e) => (
                    <motion.span
                        key={e.id}
                        className="absolute text-xl"
                        style={{ top: '30%' }}
                        initial={{ opacity: 0, y: 20, x: e.x, scale: 0.5 }}
                        animate={{
                            opacity: [0, 1, 0.8, 0],
                            y: [20, -30, -50],
                            scale: [0.5, 1, 0.8],
                        }}
                        transition={{ duration: 1.2, delay: e.delay }}
                    >
                        {e.emoji}
                    </motion.span>
                ))}
            </AnimatePresence>

            {/* Sad Text - Enhanced */}
            <AnimatePresence>
                {phase === 'drifting' && (
                    <motion.div
                        className="absolute top-6 text-2xl font-bold text-gray-500"
                        style={{
                            fontFamily: 'Comic Sans MS, cursive',
                            textShadow: '2px 2px 0 white, -1px -1px 0 white',
                        }}
                        initial={{ opacity: 0, scale: 0.5, rotate: -5 }}
                        animate={{ opacity: 1, scale: [0.5, 1.1, 1], rotate: [0, 3, 0] }}
                        transition={{ delay: 0.2, type: 'spring' }}
                    >
                        {sadText}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating dots (empty air) - enhanced */}
            <AnimatePresence>
                {phase === 'revealing' && Array.from({ length: 8 }).map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 bg-gray-300 rounded-full"
                        initial={{
                            x: -70 + Math.random() * 140,
                            y: 30,
                            opacity: 0,
                            scale: 0,
                        }}
                        animate={{
                            y: -40,
                            opacity: [0, 0.5, 0],
                            scale: [0, 1, 0.5],
                        }}
                        transition={{ duration: 1.2, delay: i * 0.1 }}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};

export default DryPullAnimation;
